/**
 * SystemHealthBadge — Indicador visual de saúde dos serviços
 * CI Sistemas de Informática Ltda. — FastALPR Parking
 *
 * Variantes:
 *   compact   — badge pequeno com ponto pulsante, tooltip ao hover (para PDV)
 *   full      — painel completo com cards por serviço (para Monitoramento)
 */
import React, { useState, useRef } from 'react';
import useSystemHealth from '../hooks/useSystemHealth';
import '../styles/SystemHealth.css';

// ─── Helpers ─────────────────────────────────────────────────────────────────
function statusColor(status) {
  if (status === 'online')   return 'green';
  if (status === 'degraded') return 'yellow';
  if (status === 'offline')  return 'red';
  return 'gray';
}

function statusLabel(status) {
  if (status === 'online')   return 'Online';
  if (status === 'degraded') return 'Degradado';
  if (status === 'offline')  return 'Offline';
  if (status === 'loading')  return 'Verificando...';
  return 'Desconhecido';
}

function periodLabel(period) {
  if (!period) return null;
  const map = { diurno: '☀️ Diurno', crepusculo: '🌅 Crepúsculo', noturno: '🌙 Noturno' };
  return map[period] || period;
}

function formatUptime(seconds) {
  if (!seconds && seconds !== 0) return null;
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}min`;
  return `${m}min`;
}

// ─── Onda 95: os vigias ──────────────────────────────────────────────────────
//
// `recognition` (Onda 61) e `entry_flow` (Onda 95) respondem perguntas que os
// quatro serviços clássicos não respondem: o reconhecimento está ACONTECENDO? a
// entrada está ABRINDO TICKETS? Entravam no consolidado desde a Onda 61, mas a
// tela nunca os mostrou — o badge ficava vermelho sem dizer por quê. Em
// `unknown` (vigia não instalado, ou outra vertical) não aparecem.
const VIGIAS = [
  { service: 'recognition', nome: 'Reconhecimento',   icone: '👁️' },
  { service: 'entry_flow',  nome: 'Fluxo de entrada', icone: '🚗' },
  // Onda 115: qual câmera está sem imagem, há quanto tempo e a causa provável.
  { service: 'camera_signal', nome: 'Sinal das câmeras', icone: '📡' },
];

function vigiasDe(services) {
  return VIGIAS
    .map(v => ({ ...v, dados: services.find(s => s.service === v.service) }))
    .filter(v => v.dados && v.dados.status !== 'unknown');
}

// O aviso diz QUAL serviço caiu e o que ele reporta. "Um ou mais serviços
// ficaram offline" manda o operador procurar; a mensagem do vigia já é a pista.
function avisoDeQueda(data, generico) {
  const ruim = (data?.services || []).find(s => (s.status === 'offline' || s.status === 'degraded') && s.message);
  return ruim ? `⚠️ ${ruim.label || ruim.service}: ${ruim.message}` : generico;
}

// ─── Variante compacta (PDV) ──────────────────────────────────────────────────
export function SystemHealthCompact({ intervalMs = 30000 }) {
  const [toastVisible, setToastVisible] = useState(false);
  const [toastMsg, setToastMsg]         = useState('');
  const [tooltipOpen, setTooltipOpen]   = useState(false);
  const toastTimerRef = useRef(null);

  const handleStatusChange = ({ previous, current, data }) => {
    if (previous === 'online' && current !== 'online') {
      setToastMsg(avisoDeQueda(data, '⚠️ Atenção: um ou mais serviços ficaram offline!'));
      setToastVisible(true);
      clearTimeout(toastTimerRef.current);
      toastTimerRef.current = setTimeout(() => setToastVisible(false), 6000);
    } else if (previous !== 'online' && current === 'online') {
      setToastMsg('✅ Todos os serviços voltaram ao normal.');
      setToastVisible(true);
      clearTimeout(toastTimerRef.current);
      toastTimerRef.current = setTimeout(() => setToastVisible(false), 4000);
    }
  };

  const { overall, services, lastChecked, checking, refresh } = useSystemHealth({
    intervalMs,
    onStatusChange: handleStatusChange,
  });

  const alpr    = services.find(s => s.service === 'alpr');
  const db      = services.find(s => s.service === 'database');
  const cameras = services.find(s => s.service === 'cameras');

  const color = statusColor(overall);

  return (
    <>
      {/* Toast de alerta */}
      {toastVisible && (
        <div className={`sh-toast sh-toast-${color}`}>
          <span>{toastMsg}</span>
          <button className="sh-toast-close" onClick={() => setToastVisible(false)}>✕</button>
        </div>
      )}

      {/* Badge compacto */}
      <div
        className={`sh-compact sh-compact-${color}`}
        onMouseEnter={() => setTooltipOpen(true)}
        onMouseLeave={() => setTooltipOpen(false)}
        onClick={() => setTooltipOpen(v => !v)}
        title="Status dos serviços"
      >
        <span className={`sh-dot sh-dot-${color} ${overall !== 'online' ? 'sh-dot-pulse' : ''}`} />
        <span className="sh-compact-label">
          {overall === 'loading' ? 'Verificando...' : overall === 'online' ? 'Sistemas OK' : overall === 'degraded' ? 'Degradado' : 'OFFLINE'}
        </span>
        {/* Onda 60: renderizado sempre; só a opacidade muda. Entrar e sair do
            fluxo a cada verificação empurrava o texto ao lado. */}
        <span className={`sh-spinner ${checking ? 'sh-spinner-on' : ''}`} aria-hidden="true" />

        {/* Tooltip expandido */}
        {tooltipOpen && (
          <div className="sh-tooltip" onClick={e => e.stopPropagation()}>
            <div className="sh-tooltip-header">
              <span className="sh-tooltip-title">Status dos Serviços</span>
              <button className="sh-tooltip-refresh" onClick={refresh} title="Atualizar agora">↻</button>
            </div>
            <div className="sh-tooltip-services">
              {/* Motor ALPR */}
              <div className={`sh-tooltip-row sh-row-${statusColor(alpr?.status)}`}>
                <span className={`sh-dot sh-dot-${statusColor(alpr?.status)}`} />
                <span className="sh-tooltip-svc-name">Motor ALPR</span>
                <span className="sh-tooltip-svc-status">{statusLabel(alpr?.status)}</span>
              </div>
              {alpr?.status === 'online' && alpr?.current_period && (
                <div className="sh-tooltip-detail">
                  {periodLabel(alpr.current_period)} · Threshold: <strong>{alpr.current_threshold}</strong>
                  {alpr.cameras_total > 0 && ` · ${alpr.cameras_active}/${alpr.cameras_total} câmeras`}
                </div>
              )}
              {/* Banco de dados */}
              <div className={`sh-tooltip-row sh-row-${statusColor(db?.status)}`}>
                <span className={`sh-dot sh-dot-${statusColor(db?.status)}`} />
                <span className="sh-tooltip-svc-name">Banco de Dados</span>
                <span className="sh-tooltip-svc-status">{statusLabel(db?.status)}</span>
              </div>
              {/* Câmeras */}
              <div className={`sh-tooltip-row sh-row-${statusColor(cameras?.status)}`}>
                <span className={`sh-dot sh-dot-${statusColor(cameras?.status)}`} />
                <span className="sh-tooltip-svc-name">Câmeras</span>
                <span className="sh-tooltip-svc-status">{cameras?.message || statusLabel(cameras?.status)}</span>
              </div>
              {/* Onda 95: vigias — o que está por trás de um badge vermelho com tudo "online" */}
              {vigiasDe(services).map(v => (
                <React.Fragment key={v.service}>
                  <div className={`sh-tooltip-row sh-row-${statusColor(v.dados.status)}`}>
                    <span className={`sh-dot sh-dot-${statusColor(v.dados.status)}`} />
                    <span className="sh-tooltip-svc-name">{v.nome}</span>
                    <span className="sh-tooltip-svc-status">{statusLabel(v.dados.status)}</span>
                  </div>
                  {v.dados.status !== 'online' && v.dados.message && (
                    <div className="sh-tooltip-detail">{v.dados.message}</div>
                  )}
                </React.Fragment>
              ))}
            </div>
            {lastChecked && (
              <div className="sh-tooltip-footer">
                Atualizado às {lastChecked.toLocaleTimeString('pt-BR')} · a cada 30s
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}

// ─── Variante completa (Monitoramento) ────────────────────────────────────────
export function SystemHealthPanel({ intervalMs = 30000 }) {
  const [toastVisible, setToastVisible] = useState(false);
  const [toastMsg, setToastMsg]         = useState('');
  const [toastType, setToastType]       = useState('error');
  const toastTimerRef = useRef(null);

  const handleStatusChange = ({ previous, current, data }) => {
    if (previous === 'online' && current !== 'online') {
      setToastMsg(avisoDeQueda(data, '⚠️ Um ou mais serviços ficaram offline!'));
      setToastType('error');
      setToastVisible(true);
      clearTimeout(toastTimerRef.current);
      toastTimerRef.current = setTimeout(() => setToastVisible(false), 8000);
    } else if (previous !== 'online' && current === 'online') {
      setToastMsg('✅ Todos os serviços voltaram ao normal.');
      setToastType('success');
      setToastVisible(true);
      clearTimeout(toastTimerRef.current);
      toastTimerRef.current = setTimeout(() => setToastVisible(false), 5000);
    }
  };

  const { overall, services, lastChecked, checking, refresh } = useSystemHealth({
    intervalMs,
    onStatusChange: handleStatusChange,
  });

  const backend = services.find(s => s.service === 'backend');
  const db      = services.find(s => s.service === 'database');
  const alpr    = services.find(s => s.service === 'alpr');
  const cameras = services.find(s => s.service === 'cameras');

  const overallColor = statusColor(overall);

  return (
    <div className="sh-panel">
      {/* Toast */}
      {toastVisible && (
        <div className={`sh-toast sh-toast-${toastType === 'error' ? 'red' : 'green'} sh-toast-panel`}>
          <span>{toastMsg}</span>
          <button className="sh-toast-close" onClick={() => setToastVisible(false)}>✕</button>
        </div>
      )}

      {/* Cabeçalho do painel */}
      <div className="sh-panel-header">
        <div className="sh-panel-title-row">
          <span className={`sh-dot sh-dot-lg sh-dot-${overallColor} ${overall !== 'online' ? 'sh-dot-pulse' : ''}`} />
          <span className="sh-panel-title">Saúde do Sistema</span>
          <span className={`sh-overall-badge sh-badge-${overallColor}`}>
            {statusLabel(overall)}
          </span>
        </div>
        <div className="sh-panel-meta">
          {lastChecked && <span>Verificado às {lastChecked.toLocaleTimeString('pt-BR')}</span>}
          <button className={`sh-refresh-btn ${checking ? 'sh-refresh-spinning' : ''}`} onClick={refresh} title="Atualizar agora">↻</button>
        </div>
      </div>

      {/* Cards de serviços */}
      <div className="sh-panel-cards">

        {/* Backend Node.js */}
        <div className={`sh-service-card sh-card-${statusColor(backend?.status)}`}>
          <div className="sh-card-top">
            <span className="sh-card-icon">⚙️</span>
            <div className="sh-card-info">
              <span className="sh-card-name">Backend API</span>
              <span className={`sh-card-status sh-status-${statusColor(backend?.status)}`}>{statusLabel(backend?.status)}</span>
            </div>
            <span className={`sh-dot sh-dot-${statusColor(backend?.status)}`} />
          </div>
          {backend?.uptime != null && (
            <div className="sh-card-detail">Uptime: {formatUptime(backend.uptime)}</div>
          )}
        </div>

        {/* Banco de dados */}
        <div className={`sh-service-card sh-card-${statusColor(db?.status)}`}>
          <div className="sh-card-top">
            <span className="sh-card-icon">🗄️</span>
            <div className="sh-card-info">
              <span className="sh-card-name">Banco de Dados</span>
              <span className={`sh-card-status sh-status-${statusColor(db?.status)}`}>{statusLabel(db?.status)}</span>
            </div>
            <span className={`sh-dot sh-dot-${statusColor(db?.status)}`} />
          </div>
          <div className="sh-card-detail">{db?.message || '—'}</div>
        </div>

        {/* Motor ALPR */}
        <div className={`sh-service-card sh-card-${statusColor(alpr?.status)}`}>
          <div className="sh-card-top">
            <span className="sh-card-icon">🤖</span>
            <div className="sh-card-info">
              <span className="sh-card-name">Motor ALPR</span>
              <span className={`sh-card-status sh-status-${statusColor(alpr?.status)}`}>{statusLabel(alpr?.status)}</span>
            </div>
            <span className={`sh-dot sh-dot-${statusColor(alpr?.status)}`} />
          </div>
          {alpr?.status === 'online' ? (
            <div className="sh-card-alpr-details">
              {alpr.current_period && (
                <span className={`sh-period-chip sh-period-${alpr.current_period}`}>
                  {periodLabel(alpr.current_period)}
                </span>
              )}
              {alpr.current_threshold && (
                <span className="sh-threshold-chip">Threshold: <strong>{alpr.current_threshold}</strong></span>
              )}
              {alpr.uptime != null && (
                <span className="sh-card-detail-item">Uptime: {formatUptime(alpr.uptime)}</span>
              )}
              {alpr.frames_processed != null && (
                <span className="sh-card-detail-item">Frames: {alpr.frames_processed.toLocaleString('pt-BR')}</span>
              )}
              {alpr.detections_total != null && (
                <span className="sh-card-detail-item">Detecções: {alpr.detections_total.toLocaleString('pt-BR')}</span>
              )}
            </div>
          ) : (
            <div className="sh-card-detail sh-card-error">{alpr?.message || 'Sem resposta'}</div>
          )}
        </div>

        {/* Câmeras */}
        <div className={`sh-service-card sh-card-${statusColor(cameras?.status)}`}>
          <div className="sh-card-top">
            <span className="sh-card-icon">📷</span>
            <div className="sh-card-info">
              <span className="sh-card-name">Câmeras</span>
              <span className={`sh-card-status sh-status-${statusColor(cameras?.status)}`}>
                {cameras?.total > 0 ? `${cameras.active}/${cameras.total} ativas` : statusLabel(cameras?.status)}
              </span>
            </div>
            <span className={`sh-dot sh-dot-${statusColor(cameras?.status)}`} />
          </div>
          {cameras?.offline?.length > 0 && (
            <div className="sh-card-offline-list">
              {cameras.offline.map(cam => (
                <span key={cam.id} className="sh-offline-cam">⚠️ {cam.name}</span>
              ))}
            </div>
          )}
          {cameras?.total === 0 && (
            <div className="sh-card-detail">Nenhuma câmera cadastrada</div>
          )}
        </div>

        {/* Onda 95: vigias */}
        {vigiasDe(services).map(v => (
          <div key={v.service} className={`sh-service-card sh-card-${statusColor(v.dados.status)}`}>
            <div className="sh-card-top">
              <span className="sh-card-icon">{v.icone}</span>
              <div className="sh-card-info">
                <span className="sh-card-name">{v.nome}</span>
                <span className={`sh-card-status sh-status-${statusColor(v.dados.status)}`}>{statusLabel(v.dados.status)}</span>
              </div>
              <span className={`sh-dot sh-dot-${statusColor(v.dados.status)}`} />
            </div>
            <div className={`sh-card-detail ${v.dados.status === 'offline' ? 'sh-card-error' : ''}`}>{v.dados.message || '—'}</div>
            {v.dados.status !== 'online' && v.dados.action && (
              <div className="sh-card-detail"><strong>O que fazer:</strong> {v.dados.action}</div>
            )}
          </div>
        ))}

      </div>
    </div>
  );
}

export default SystemHealthCompact;
